import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminProvider, useAdmin } from "./context/AdminContext";
import { MembershipProvider } from "./context/MembershipContext";
import { NotificationProvider } from "./context/NotificationContext";
import { Toaster } from "./components/ui/sonner";
import NotificationPrompt from "./components/NotificationPrompt";

// User Pages
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Grid from "./pages/Grid";
import RightNow from "./pages/RightNow";
import Profile from "./pages/Profile";
import MyProfile from "./pages/MyProfile";
import Messages from "./pages/Messages";
import Chat from "./pages/Chat";
import Activity from "./pages/Activity";
import Notifications from "./pages/Notifications";
import Blocked from "./pages/Blocked";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmail from "./pages/VerifyEmail";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FAQ from "./pages/FAQ";
import Safety from "./pages/Safety";
import Resources from "./pages/Resources";
import Verify from "./pages/Verify";
import Contact from "./pages/Contact";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminReports from "./pages/admin/AdminReports";
import AdminReportDetail from "./pages/admin/AdminReportDetail";
import AdminAuditLogs from "./pages/admin/AdminAuditLogs";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminVerifications from "./pages/admin/AdminVerifications";
import AdminMemberships from "./pages/admin/AdminMemberships";
import AdminPhotoModeration from "./pages/admin/AdminPhotoModeration";
import AdminContact from "./pages/admin/AdminContact";

// Protected Route wrapper (User)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to grid if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/grid" replace />;
  }
  
  return children;
};

// Admin Protected Route
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAdmin();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function UserRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        <PublicRoute>
          <Landing />
        </PublicRoute>
      } />
      <Route path="/auth" element={
        <PublicRoute>
          <Auth />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/help" element={<FAQ />} />
      
      {/* Protected routes */}
      <Route path="/grid" element={
        <ProtectedRoute>
          <Grid />
        </ProtectedRoute>
      } />
      <Route path="/right-now" element={
        <ProtectedRoute>
          <RightNow />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/my-profile" element={
        <ProtectedRoute>
          <MyProfile />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/chat/:recipientId" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/activity" element={
        <ProtectedRoute>
          <Activity />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />
      <Route path="/blocked" element={
        <ProtectedRoute>
          <Blocked />
        </ProtectedRoute>
      } />
      <Route path="/safety" element={
        <ProtectedRoute>
          <Safety />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute>
          <Resources />
        </ProtectedRoute>
      } />
      <Route path="/verify" element={
        <ProtectedRoute>
          <Verify />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <Events />
        </ProtectedRoute>
      } />
      <Route path="/events/:eventId" element={
        <ProtectedRoute>
          <EventDetail />
        </ProtectedRoute>
      } />
      <Route path="/contact" element={
        <ProtectedRoute>
          <Contact />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/" element={
        <AdminProtectedRoute>
          <AdminLayout />
        </AdminProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:userId" element={<AdminUserDetail />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="reports/:reportId" element={<AdminReportDetail />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="verifications" element={<AdminVerifications />} />
        <Route path="memberships" element={<AdminMemberships />} />
        <Route path="moderation" element={<AdminPhotoModeration />} />
        <Route path="contact" element={<AdminContact />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Admin routes - separate context */}
          <Route path="/admin/*" element={
            <AdminProvider>
              <AdminRoutes />
            </AdminProvider>
          } />
          
          {/* User routes */}
          <Route path="/*" element={
            <AuthProvider>
              <MembershipProvider>
                <NotificationProvider>
                  <UserRoutes />
                </NotificationProvider>
              </MembershipProvider>
            </AuthProvider>
          } />
        </Routes>
        <NotificationPrompt />
        <Toaster 
          position="top-center" 
          toastOptions={{
            style: {
              background: 'rgba(10, 10, 10, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#EDEDED',
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
